import { ErrorTypes, Eva2Error } from '~/lib/errors/Eva2Error';

export const MultiStepContextType = 'multi-step';

export type MultiStepContext = {
  type: string;
  url: string;
  forceRefreshProfileOnComplete: boolean;
  checkValidatedFormOnMounted: boolean;
  refreshProfiling?: string;
};

export const getFormMultiStepContext = (
  url: string,
  forceRefreshProfileOnComplete: boolean = true,
  checkValidatedFormOnMounted: boolean = false
): MultiStepContext => {
  return {
    type: FormStrategyType.MultiStep,
    url,
    forceRefreshProfileOnComplete,
    checkValidatedFormOnMounted,
  };
};

export const getFormRefreshStepContext = (
  url: string,
  refreshProfiling: string,
  forceRefreshProfileOnComplete: boolean = true,
  checkValidatedFormOnMounted: boolean = false
): MultiStepContext => {
  return {
    type: FormStrategyType.MultiStep,
    url,
    refreshProfiling,
    forceRefreshProfileOnComplete,
    checkValidatedFormOnMounted,
  };
};

export const useFormStrategyMultiStep = (
  context: MultiStepContext
): UseFormStrategy => {
  const data = ref<any>({});

  const completed = ref<boolean>(false);

  const error = ref<Eva2Error>(null);

  const updated = ref<boolean>(true);

  const loading = ref<boolean>(false);

  const submitEvent = createEventHook<any>();

  const formNavigation = useFormNavigationObserver();

  const profileLoadFromNetwork = useProfileLoadFromNetwork();

  const count = computed<number>(() => {
    return data.value?.numberOfSteps || 1;
  });

  const checkValidatedFormOnMounted = computed(
    () => context.checkValidatedFormOnMounted
  );

  const index = computed<number>(() => {
    return data.value?.step?.index || 0;
  });

  const step = computed(() => {
    return data.value?.step;
  });

  const fields = computed(() => {
    return step.value?.fields ?? [];
  });

  const baseTitle = computed(() => {
    if (step.value?.recap) {
      return 'Vérifiez vos informations et modifiez-les si besoin :';
    }
    return step.value?.title ?? '';
  });

  const inputFields = computed<FormField[]>(() => {
    if (!fields.value) return [];
    fields.value.filter((field) => field.validation);
  });

  const title = computed(() => {
    if (
      hasFieldsReaquired.value &&
      inputFields?.value?.length === 1 &&
      inputFields.value[0].type === 'select'
    ) {
      return `${baseTitle.value}*`;
    }
    return baseTitle.value;
  });

  const hasPrevious = computed(() => data.value.previousStep !== null);

  const hasCancel = computed(() => false);

  const fetch = async (body: any = null) => {
    if (loading.value) return;
    if (!body && context.refreshProfiling) {
      body = {
        refreshProfiling: context.refreshProfiling,
      };
    }
    loading.value = true;
    completed.value = false;
    const {
      result,
      error: errorRequest,
      status,
    } = await useApiFetch(context.url, {
      method: 'POST',
      body,
    });

    if (errorRequest) {
      error.value = new Eva2Error(
        ErrorTypes.Adam,
        {},
        errorRequest.response?._data?.summary || "une erreur s'est produite"
      );
    } else if (status === 202) {
      if (context.forceRefreshProfileOnComplete) {
        await profileLoadFromNetwork.run();
      }
      loading.value = false;
      completed.value = true;
    } else {
      data.value = result;
    }
    loading.value = false;
  };

  const releaseError = () => {
    error.value = null;
  };

  const getSubmitBody = () => {
    const body = {
      ...data.value,
      step: {
        id: step.value.id,
        index: step.value.index,
        memberValues: {
          ...toRaw(formFieldsData.memberValues.value),
        },
      },
    };
    return body;
  };

  const submit = async () => {
    const request = await fetch(getSubmitBody());
    submitEvent.trigger(request);
  };

  const previous = async () => {
    if (!data.value.previousStep) return;
    const body = {
      ...data.value,
      goBackFrom: step.value?.id,
      goBackTo: data.value.previousStep,
    };
    delete body.step;
    await fetch(body);
  };

  const gotoStep = async (stepId) => {
    const body = {
      ...data.value,
      goBackFrom: 'recap',
      goBackTo: stepId,
    };
    delete body.step;
    await fetch(body);
  };

  const formFieldsData = useFormFieldsData(step);

  const formFieldsValidation = useFormFieldsValidation(
    fields,
    formFieldsData.memberValues
  );

  const ready = computed(() => {
    if (!step.value) return false;
    return (
      step.value?.id === formFieldsData.id.value ||
      formFieldsRecap.isRecap.value
    );
  });

  const formFieldsRecap = useFormFieldsRecap(step);

  const { hasFieldsReaquired } = useFormFieldsIsRequired(fields);

  return {
    context,
    fetch,
    submit,
    previous,
    gotoStep,
    step,
    index,
    count,
    title,
    fields,
    hasPrevious,
    hasCancel,
    formFieldsData,
    formFieldsValidation,
    formFieldsRecap,
    ready,
    completed,
    updated,
    loading,
    hasFieldsReaquired,
    onSubmitComplete: submitEvent.on,
    checkValidatedFormOnMounted,
    getSubmitBody,
    formNavigation,
    releaseError,
    error,
  };
};
